/**
 * Billing Settings Page
 *
 * Displays subscription status and allows users to:
 * - Subscribe to Plus/Pro via Stripe Checkout with 14-day trial
 * - Manage subscription via Stripe Billing Portal
 * - Switch tiers during trial
 * - View refund eligibility
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Zap,
  Check,
  ExternalLink,
  Loader2,
  AlertCircle,
  Calendar,
  Crown,
  CreditCard,
  Clock,
  RefreshCw,
  Shield,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/client/hooks/use-auth';
import { cn, formatDate } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { getReferralCode, clearReferralCode } from '@/client/lib/referral-tracking';
import { SettingsLayout } from '@/client/components/layout/settings-layout';

interface BillingInfo {
  tier: 'free' | 'plus' | 'pro';
  status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing' | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isPlusUser: boolean;
  isProUser: boolean;
  // Trial info
  isTrialing: boolean;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  isTrialEligible: boolean;
  // Refund info
  refundEligibleUntil: string | null;
  isRefundEligible: boolean;
  refundDaysRemaining: number | null;
}

export function BillingPage() {
  const { isPlusUser: hasPlus } = useAuth();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [selectedTier, setSelectedTier] = useState<'plus' | 'pro'>('plus');
  const billingPeriod = 'yearly' as const;

  // Check for tier param in URL
  useEffect(() => {
    const tierParam = searchParams.get('tier');
    if (tierParam === 'pro') {
      setSelectedTier('pro');
    }
  }, [searchParams]);

  // Show success/canceled toast based on URL params
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Welcome! Your subscription is now active.');
      // Refresh billing info
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Checkout was canceled. You can try again anytime.');
    }
  }, [searchParams, queryClient]);

  // Fetch billing info
  const { data: billing, isLoading } = useQuery<BillingInfo>({
    queryKey: ['billing'],
    queryFn: async () => {
      const response = await apiFetch('/api/billing');
      if (!response.ok) throw new Error('Failed to fetch billing info');
      return response.json();
    },
  });

  // Create checkout session
  const checkoutMutation = useMutation({
    mutationFn: async ({
      tier,
      period,
    }: {
      tier: 'plus' | 'pro';
      period: 'monthly' | 'yearly';
    }) => {
      // Get referral code from tracking (URL param > localStorage > cookie)
      const referralCode = getReferralCode();

      const response = await apiFetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier,
          billingPeriod: period,
          includeTrial: false, // Trials temporarily disabled
          referralCode: referralCode || undefined,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create checkout session');
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Clear referral tracking on successful checkout redirect
      clearReferralCode();
      // Redirect to Stripe Checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
    },
  });

  // Switch tier during trial
  const switchTierMutation = useMutation({
    mutationFn: async (newTier: 'plus' | 'pro') => {
      const response = await apiFetch('/api/billing/switch-tier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: newTier }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to switch tier');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(
        `Switched to ${data.tier === 'pro' ? 'Pro' : 'Plus'}! Your trial continues until ${formatDate(new Date(data.trialEndsAt))}.`
      );
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to switch tier');
    },
  });

  // Create billing portal session
  const portalMutation = useMutation({
    mutationFn: async () => {
      const response = await apiFetch('/api/billing/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create portal session');
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Redirect to Stripe Billing Portal
      if (data.portalUrl) {
        window.location.href = data.portalUrl;
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to open billing portal');
    },
  });

  const handleSubscribe = (tier: 'plus' | 'pro') => {
    checkoutMutation.mutate({ tier, period: billingPeriod });
  };

  const handleSwitchTier = (newTier: 'plus' | 'pro') => {
    switchTierMutation.mutate(newTier);
  };

  const handleManageBilling = () => {
    portalMutation.mutate();
  };

  const plusFeatures = [
    '1-year analytics history',
    'Custom short codes (vanity URLs)',
    'Link folders for organization',
    'Branded QR codes (no watermark)',
    'API access',
    'Share analytics (25 shares)',
    'Advanced analytics & exports',
  ];

  const proFeatures = [
    'Everything in Plus',
    'Unlimited analytics history',
    'Unlimited team shares',
    'Team members (up to 10)',
    'Webhook notifications',
    'Advanced routing rules',
    'Priority support',
  ];

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  const isSubscribed = billing?.isPlusUser || billing?.isProUser || hasPlus;
  const isPastDue = billing?.status === 'past_due';
  const isCanceling = billing?.cancelAtPeriodEnd;
  const isTrialing = billing?.isTrialing;
  const currentTier = billing?.tier || 'free';
  const tierLabel = currentTier === 'pro' ? 'Pro' : currentTier === 'plus' ? 'Plus' : 'Free';

  return (
    <SettingsLayout>
      {/* Trial Banner */}
      {isTrialing && billing?.trialDaysRemaining !== null && (
        <div className="rounded-lg border-2 border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Clock className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="font-semibold text-green-700">
                  {billing.trialDaysRemaining} {billing.trialDaysRemaining === 1 ? 'day' : 'days'}{' '}
                  left in your trial
                </p>
                <p className="text-sm text-muted-foreground">
                  Your {tierLabel} trial ends on{' '}
                  {billing.trialEndsAt && formatDate(new Date(billing.trialEndsAt))}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {currentTier === 'plus' ? (
                <button
                  onClick={() => handleSwitchTier('pro')}
                  disabled={switchTierMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-600 text-sm font-medium hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                >
                  {switchTierMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Upgrade to Pro
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => handleSwitchTier('plus')}
                  disabled={switchTierMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-sm font-medium hover:bg-muted/80 transition-colors disabled:opacity-50"
                >
                  {switchTierMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Switch to Plus
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Refund Eligibility Banner */}
      {!isTrialing &&
        isSubscribed &&
        billing?.isRefundEligible &&
        billing?.refundDaysRemaining !== null && (
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Shield className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="font-medium text-blue-700">30-day money-back guarantee active</p>
                <p className="text-sm text-muted-foreground">
                  You have {billing.refundDaysRemaining}{' '}
                  {billing.refundDaysRemaining === 1 ? 'day' : 'days'} to request a full refund.
                  Contact us at{' '}
                  <a href="mailto:support@armenian.link" className="text-blue-600 hover:underline">
                    support@armenian.link
                  </a>{' '}
                  if needed.
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Current Plan */}
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Current Plan</h2>
            {isSubscribed && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                  currentTier === 'pro'
                    ? 'bg-purple-500/20 text-purple-600'
                    : 'bg-amber-500/20 text-amber-600'
                )}
              >
                <Crown className="h-3 w-3" />
                {tierLabel}
                {isTrialing && ' (Trial)'}
              </span>
            )}
          </div>
          {isSubscribed && !isTrialing && (
            <button
              onClick={handleManageBilling}
              disabled={portalMutation.isPending}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {portalMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <ExternalLink className="h-4 w-4" />
                  Manage Billing
                </>
              )}
            </button>
          )}
        </div>

        {isSubscribed ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  currentTier === 'pro'
                    ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20'
                    : 'bg-gradient-to-br from-amber-500/20 to-orange-500/20'
                )}
              >
                <Zap
                  className={cn(
                    'h-5 w-5',
                    currentTier === 'pro' ? 'text-purple-500' : 'text-amber-500'
                  )}
                />
              </div>
              <div>
                <p className="font-medium">{tierLabel} Plan</p>
                <p className="text-sm text-muted-foreground">
                  {currentTier === 'pro' ? 'All features unlocked' : 'Premium features unlocked'}
                </p>
              </div>
            </div>

            {isPastDue && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-600">Payment Past Due</p>
                  <p className="text-sm text-muted-foreground">
                    Please update your payment method to continue using {tierLabel} features.
                  </p>
                  <button
                    onClick={handleManageBilling}
                    className="text-sm text-red-600 hover:underline mt-1"
                  >
                    Update payment method
                  </button>
                </div>
              </div>
            )}

            {isCanceling && billing?.currentPeriodEnd && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Calendar className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-600">Subscription Ending</p>
                  <p className="text-sm text-muted-foreground">
                    Your {tierLabel} subscription will end on{' '}
                    <span className="font-medium">
                      {formatDate(new Date(billing.currentPeriodEnd))}
                    </span>
                    . You&apos;ll retain access until then.
                  </p>
                  <button
                    onClick={handleManageBilling}
                    className="text-sm text-amber-600 hover:underline mt-1"
                  >
                    Resume subscription
                  </button>
                </div>
              </div>
            )}

            {billing?.currentPeriodEnd && !isCanceling && !isPastDue && !isTrialing && (
              <p className="text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 inline mr-1" />
                Next billing date: {formatDate(new Date(billing.currentPeriodEnd))}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">Free Plan</p>
                <p className="text-sm text-muted-foreground">Basic features for personal use</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Start a 14-day free trial to unlock premium features.
            </p>
          </div>
        )}
      </div>

      {/* Upgrade Section (only show for free users) */}
      {!isSubscribed && (
        <div className="space-y-4">
          <div className="text-center">
            <h3 className="text-xl font-bold mb-2">Start Your 14-Day Free Trial</h3>
            <p className="text-muted-foreground">
              Try all premium features free. No charge until your trial ends.
            </p>
          </div>

          {/* Tier Selection Tabs */}
          <div className="flex rounded-lg bg-muted p-1">
            <button
              onClick={() => setSelectedTier('plus')}
              className={cn(
                'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors',
                selectedTier === 'plus'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Plus - $24/year
            </button>
            <button
              onClick={() => setSelectedTier('pro')}
              className={cn(
                'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors',
                selectedTier === 'pro'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Pro - $60/year
            </button>
          </div>

          {/* Selected Tier Card */}
          <div
            className={cn(
              'rounded-lg border-2 p-6',
              selectedTier === 'pro'
                ? 'border-purple-500/30 bg-gradient-to-b from-purple-500/10 to-pink-500/5'
                : 'border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-orange-500/5'
            )}
          >
            {/* Price Display */}
            <div className="text-center mb-6">
              <div className="text-4xl font-bold">
                {selectedTier === 'pro' ? '$60' : '$24'}
                <span className="text-lg font-normal text-muted-foreground">/year</span>
              </div>
              <p
                className={cn(
                  'text-sm mt-1',
                  selectedTier === 'pro' ? 'text-purple-600' : 'text-amber-600'
                )}
              >
                {selectedTier === 'pro' ? "That's just $5/month!" : "That's just $2/month!"}
              </p>
              <p className="text-sm text-green-600 font-medium mt-2">14-day free trial included</p>
            </div>

            {/* Features List */}
            <div className="grid gap-2 mb-6">
              {(selectedTier === 'pro' ? proFeatures : plusFeatures).map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {/* Subscribe Button */}
            <button
              onClick={() => handleSubscribe(selectedTier)}
              disabled={checkoutMutation.isPending}
              className={cn(
                'w-full inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                selectedTier === 'pro'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400'
              )}
            >
              {checkoutMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Zap className="h-5 w-5" />
                  Start Free Trial
                </>
              )}
            </button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              14-day free trial. Cancel anytime. One trial per customer.
            </p>
          </div>
        </div>
      )}

      {/* Features Comparison (show for everyone) */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Plus Features */}
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold">Plus Features</h3>
            {currentTier === 'plus' && (
              <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 text-xs font-medium">
                Current
              </span>
            )}
          </div>
          <div className="grid gap-3">
            {plusFeatures.map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-5 h-5 rounded-full flex items-center justify-center',
                    currentTier === 'plus' || currentTier === 'pro' ? 'bg-green-500/20' : 'bg-muted'
                  )}
                >
                  {currentTier === 'plus' || currentTier === 'pro' ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                  )}
                </div>
                <span
                  className={cn(
                    'text-sm',
                    currentTier === 'plus' || currentTier === 'pro'
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  )}
                >
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Pro Features */}
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold">Pro Features</h3>
            {currentTier === 'pro' && (
              <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-600 text-xs font-medium">
                Current
              </span>
            )}
          </div>
          <div className="grid gap-3">
            {proFeatures.map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-5 h-5 rounded-full flex items-center justify-center',
                    currentTier === 'pro' ? 'bg-green-500/20' : 'bg-muted'
                  )}
                >
                  {currentTier === 'pro' ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                  )}
                </div>
                <span
                  className={cn(
                    'text-sm',
                    currentTier === 'pro' ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* View Pricing Link */}
      <div className="text-center">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          View full pricing comparison
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </SettingsLayout>
  );
}
